import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle } from 'lucide-react';
import { describeOrgPricingGaps, type OrgPricingGaps } from '@iomi/shared';

interface MissingPricingModalProps {
  gaps: OrgPricingGaps;
  /** Admins get a button into the setup wizard; everyone else is told to ask one. */
  isAdmin: boolean;
  onSetUp: () => void;
  onContinue: () => void;
  onCancel: () => void;
}

/**
 * Shown when someone starts a new estimate while the org is missing crew rates
 * and/or packing item prices: the estimate would come out under-priced.
 */
export function MissingPricingModal({ gaps, isAdmin, onSetUp, onContinue, onCancel }: MissingPricingModalProps) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const missing = describeOrgPricingGaps(gaps) ?? '';

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="missing-pricing-title"
    >
      <div className="absolute inset-0 bg-midnight/70" onClick={onCancel} aria-hidden="true" />

      <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-6 flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle size={20} className="text-amber-700" />
          </div>
          <h2 id="missing-pricing-title" className="font-display text-h3 text-midnight font-semibold pt-1.5">
            Pricing isn&rsquo;t set up
          </h2>
        </div>

        <div className="text-small text-slate leading-relaxed flex flex-col gap-2">
          <p>
            Your company&rsquo;s <span className="font-semibold text-midnight">{missing}</span>{' '}
            haven&rsquo;t been set yet. This will affect the quoted price.
          </p>
          <p>
            If you continue, you&rsquo;ll have to set them manually
            in the estimate (View &amp; Adjust Estimate), or start a new estimate once they&rsquo;re set.
          </p>
          {!isAdmin && (
            <p>Contact your company admin to set them.</p>
          )}
        </div>

        <div className="flex flex-col gap-2.5 pt-1">
          {isAdmin && (
            <button type="button" onClick={onSetUp} className="btn-primary w-full min-h-[44px]">
              Go to Setup
            </button>
          )}
          <button
            type="button"
            onClick={onContinue}
            className={`${isAdmin ? 'btn-secondary' : 'btn-primary'} w-full min-h-[44px]`}
          >
            Create Estimate Anyway
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full min-h-[44px] text-sm font-semibold text-slate hover:text-midnight transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
